import React, { useEffect, useRef, useState } from "react";
import { getAllVehicles } from "../Service/VehicleService";
import { getAllSources } from "../Service/SourceService";
import { getALLDestinations } from "../Service/DestinationService";
import { getAllFixedSources } from "../Service/FixedSourceService";
import { getALLFixedDestinations } from "../Service/FixedDestinationService";
import { getDistanceRate } from "../Service/tripDistance";
import { getAmountBySourceAndDestination } from "../Service/FixedRateService";
import { getVehicleAdvanceByVehicleIdAndDate } from "../Service/VehicleAdvanceService";
import Main from "../Components/Main";
import PageContainer from "../Components/PageContainer";
import CollapseCard from "../Components/CollapseCard";
import { useToast } from "../Components/toast/ToastProvider";
import "../Style/toggle.css";
import BillingInformationList from "../Components/BillingInformationList";
import { forwardRef } from "react";
import { deleteBill, getBillsByFilter, saveBill } from "../Service/BillService";
import { getAllVendors } from "../Service/VendorService";
import { deleteTrip } from "../Service/TripService";

const BillingInformation = () => {
  const toast = useToast();
  const [date, setDate] = useState(() => {
    const today = new Date().toISOString().split("T")[0];
    return today;
  });
  const [vendors, setVendors] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [rateType, setRateType] = useState("km");
  const [sources, setSources] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [fixSources, setFixSources] = useState([]);
  const [fixDestinations, setFixDestinations] = useState([]);
  const [billingId, setBillingId] = useState(0);
  const [billingTrip, setBillingTrip] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [filteredVehicles, setFilteredVehicles] = useState([]);

  useEffect(() => {
    fetchDropdowns();
    setBillingTrip(
      [...Array(50)].map(() => ({
        date: "",
        vehicle_id: "",
        vehicle_number: "",
        source_name: "",
        destination_name: "",
        rate: 0,
        total_km: 0,
        total_tones: 0,
        amount: 0,
        transition_charge: 0,
        advance_amount: 0,
        balance_amount: 0,
        remarks: "",
        id: 0,
      })),
    );
  }, []);
  const fetchDropdowns = async () => {
    try {
      const [vehRes, srcRes, destRes, fixSrcRes, fixDestRes, venRes] =
        await Promise.all([
          getAllVehicles(),
          getAllSources(),
          getALLDestinations(),
          getAllFixedSources(),
          getALLFixedDestinations(),
          getAllVendors(),
        ]);
      setVehicles(vehRes?.data || []);
      setSources(srcRes?.data || []);
      setDestinations(destRes?.data || []);
      setFixSources(fixSrcRes?.data || []);
      setFixDestinations(fixDestRes?.data || []);
      setVendors(venRes?.data || []);
    } catch (err) {
      toast(err?.message || "Failed to fetch data", "ERROR");
    }
  };

  const handleInputChange = async (index, field, value, isManual = false) => {
    const updatedTrips = [...billingTrip];
    const row = updatedTrips[index];

    if (field === "vehicle") {
      if (!value) {
        row.vehicle_id = "";
        row.vehicle_number = "";
      } else {
        row.vehicle_id = value.id;
        row.vehicle_number = value.title;
      }

      setBillingTrip(updatedTrips);
      return;
    }

    if (typeof value === "object" && value?.title) {
      value = value.title;
    }

    row[field] = value;
    if (field === "vehicle") {
      row.vehicle_id = value?.id || "";
      row.vehicle_number = value?.title || "";

      if (row.date && value?.id) {
        const advanceList = await fetchAdvanceAmount(value.id, row.date);

        row.advance_list = advanceList;
        row.advance_amount =
          advanceList.length === 1 ? advanceList[0].amount : 0;
        row.advance_id = advanceList.length === 1 ? advanceList[0].id : null;
      }

      setBillingTrip(updatedTrips);
      return;
    }

    if (field === "date" && row.vehicle_id) {
      const advanceList = await fetchAdvanceAmount(row.vehicle_id, value);
      console.log("Advance List:", advanceList);
      if (advanceList.length === 1) {
        row.advance_amount = advanceList[0].amount;
        row.advance_id = advanceList[0].id;
        row.advance_list = [];
      } else if (advanceList.length > 1) {
        row.advance_list = advanceList;
        row.advance_amount = 0;
        row.advance_id = null;
      } else {
        row.advance_amount = 0;
        row.advance_id = null;
        row.advance_list = [];
      }
    }

    if (rateType === "fixed") {
      row.total_tones = 1;
      if (field === "amount") {
        row.rate = parseFloat(value) || 0;
        row.amount = parseFloat(value) || 0;
      }
    }

    if (rateType === "km") {
      if (field === "rate" || field === "total_tones") {
        row.amount =
          (parseFloat(row.rate) || 0) * (parseFloat(row.total_tones) || 0);
      }
    }

    row.balance_amount =
      (parseFloat(row.amount) || 0) +
      (parseFloat(row.transition_charge) || 0) -
      (parseFloat(row.advance_amount) || 0);

    setBillingTrip(updatedTrips);

    if (isManual) return;

    if (
      (field === "source_name" || field === "destination_name") &&
      row.source_name &&
      row.destination_name
    ) {
      if (rateType === "fixed")
        fetchFixedRate(row.source_name, row.destination_name, index);
      if (rateType === "km")
        fetchDistanceRate(row.source_name, row.destination_name, index);
    }
  };

  const handleVendorChange = (vendorObj) => {
    if (!vendorObj) {
      setSelectedVendor(null);
      setFilteredVehicles([]);
      setSelectedVehicle(null);
      return;
    }

    setSelectedVendor(vendorObj);
    const vendorVehicle = vehicles.filter((v) => v.vendor_id === vendorObj.id);

    setFilteredVehicles(vendorVehicle);
    setSelectedVehicle(null);
    if (date) {
      fetchExistingBill(date, vendorObj.id);
    }
  };

  const handleVehicleChange = (vehicleObj) => {
    if (!vehicleObj || !vehicleObj.id) {
      setSelectedVehicle(null);
      setBillingId(0);
      resetEmptyTrips(date, null);
      return;
    }
    setSelectedVehicle(vehicleObj);
    applyVehicleChange(vehicleObj.id);
  };

  const fetchDistanceRate = async (source_name, destination_name, index) => {
    try {
      const sourceObjRate = sources.find((s) => s.title === source_name);
      const destObjRate = destinations.find(
        (d) => d.title === destination_name,
      );

      if (!sourceObjRate || !destObjRate) {
        return;
      }

      const res = await getDistanceRate(sourceObjRate.id, destObjRate.id);

      if (!res.error && res.data) {
        const updatedTrips = [...billingTrip];
        updatedTrips[index].total_km = res.data.km || 0;
        updatedTrips[index].rate = res.data.rate || 0;
        updatedTrips[index].amount =
          (res.data.rate || 0) * (updatedTrips[index].total_tones || 0);
        updatedTrips[index].balance_amount =
          updatedTrips[index].amount +
          updatedTrips[index].transition_charge -
          updatedTrips[index].advance_amount;
        setBillingTrip(updatedTrips);
      }
    } catch {
      toast("Error fetching distance rate", "ERROR");
    }
  };

  const fetchFixedRate = async (source_name, destination_name, index) => {
    try {
      const sourceObj = fixSources.find((s) => s.title === source_name);
      const destObj = fixDestinations.find((d) => d.title === destination_name);

      if (!sourceObj || !destObj) return;

      const res = await getAmountBySourceAndDestination(
        sourceObj.id,
        destObj.id,
      );

      if (!res.error && res.amount) {
        const updatedTrips = [...billingTrip];
        const row = updatedTrips[index];

        // row.rate = parseFloat(res.amount) || 0;
        row.amount = parseFloat(res.amount);
        row.rate = parseFloat(res.amount);
        row.total_tones = 1;

        row.balance_amount =
          (parseFloat(row.amount) || 0) +
          (parseFloat(row.transition_charge) || 0) -
          (parseFloat(row.advance_amount) || 0);

        setBillingTrip(updatedTrips);
      }
    } catch {
      toast("Error fetching fixed amount", "ERROR");
    }
  };

  const applyVehicleChange = async (vehicle_id) => {
    const updatedTrips = await Promise.all(
      billingTrip.map(async (row) => {
        if (!row.date) return row;
        const advanceList = await fetchAdvanceAmount(vehicle_id, row.date);

      if (advanceList.length === 1) {
          return {
          ...row,
          advance_amount: advanceList[0].amount,
          advance_id: advanceList[0].id,
          advance_list: [],
          };
        }

        if (advanceList.length > 1) {
          return {
          ...row,
          advance_amount: 0,
          advance_id: null,
          advance_list: advanceList,
          };
        }

        return {
          ...row,
          advance_amount: 0,
          advance_id: null,
          advance_list: [],
  };

        // return {
        //     ...row,
        //     advance_amount: advanceAmount,
        // };
      }),
    );

    setBillingTrip(updatedTrips);
  };

  const fetchAdvanceAmount = async (vehicle_id, selectedDate) => {
    if (!vehicle_id || !selectedDate) return [];

    try {
      const res = await getVehicleAdvanceByVehicleIdAndDate(
        vehicle_id,
        selectedDate,
      );

      const data = Array.isArray(res?.data) ? res.data : [];

      const normalize = (d) => new Date(d).setHours(0, 0, 0, 0);

      const selected = normalize(selectedDate);

      const filtered = data.filter((item) => {
        const itemDate = normalize(item.date);
        const isOpen =
          !item.status || String(item.status).toLowerCase() === "open";
        return itemDate <= selected && isOpen;
      });

      return filtered;
    } catch (err) {
      console.error("Advance fetch error", err);
      return [];
    }
  };
  const fetchExistingBill = async (billDate, vendorId) => {
    if (!billDate || !vendorId) return;
    const vendorVehicle = vehicles.filter((v) => v.vendor_id === vendorId);
    setFilteredVehicles(vendorVehicle);
    try {
      const res = await getBillsByFilter({
        date: billDate,
        vendor_id: vendorId,
      });

      const billArr = res?.data?.bill || [];
      const tripsArr = res?.data?.trips || [];

      if (!billArr.length) {
        resetEmptyTrips(billDate, null);
        return;
      }

      const bill = billArr[0];

      if (bill.status === "process") {
        toast("Bill already processed", "ERROR");
        setBillingId(0);
        resetEmptyTrips(billDate, null);
        return;
      }

      if (bill.status === "new") {
        setBillingId(bill.id);
      } else {
        resetEmptyTrips(billDate, null);
        return;
      }

      const mappedTrips = tripsArr.map((t) => {
        const vehicleObj = vehicles.find((v) => v.id === t.vehicle_id);
        return {
          id: t.id,
          date: t.date,
          vehicle_id: t.vehicle_id,
          vehicle_number: vehicleObj?.vehicle_number || "",
          source_name: t.source_name,
          destination_name: t.destination_name,
          rate: Number(t.rate) || 0,
          total_km: Number(t.total_km) || 0,
          total_tones: Number(t.total_tones) || 0,
          amount: Number(t.amount) || 0,
          transition_charge: Number(t.transition_charge) || 0,
          advance_id: t.vehicle_advance_id || null,
          advance_amount: Number(t.advance_amount) || 0,
          balance_amount: Number(t.balance_amount) || 0,
          remarks: t.remarks || "",
        };
      });
      if (mappedTrips.length > 0) {
        const firstVehicleId = mappedTrips[0].vehicle_id;

        const foundVehicle = vehicles.find((v) => v.id === firstVehicleId);

        if (foundVehicle) {
          setSelectedVehicle(foundVehicle);
        }
      }

      const emptyRows = Array.from(
        { length: Math.max(0, 50 - mappedTrips.length) },
        () => ({
          id: 0,
          date: "",
          vehicle_id: "",
          vehicle_number: "",
          source_name: "",
          destination_name: "",
          rate: 0,
          total_km: 0,
          total_tones: 0,
          amount: 0,
          transition_charge: 0,
          advance_amount: 0,
          balance_amount: 0,
          remarks: "",
        }),
      );

      setBillingTrip([...mappedTrips, ...emptyRows]);

      setRateType(
        mappedTrips.every((t) => Number(t.total_km) === 0) ? "fixed" : "km",
      );

      toast("Existing bill loaded", "SUCCESS");
    } catch {
      resetEmptyTrips(billDate, null);
      toast("Failed to fetch bill", "ERROR");
    }
  };
  const resetEmptyTrips = (billDate, vehicleId) => {
    setBillingId(0);
    const emptyTrips = [...Array(50)].map(() => ({
      date: "",
      vehicle_id: vehicleId,
      source_name: "",
      destination_name: "",
      rate: 0,
      total_km: 0,
      total_tones: 0,
      amount: 0,
      transition_charge: 0,
      advance_amount: 0,
      balance_amount: 0,
      remarks: "",
    }));

    setBillingTrip(emptyTrips);
    setRateType("km");
  };

  const handleSave = async () => {
    const validTrips = billingTrip.filter((t) => {
      if (rateType === "fixed") {
        return (
          t.source_name?.trim() !== "" &&
          t.destination_name?.trim() !== "" &&
          t.vehicle_id
        );
      }
      return (
        t.source_name?.trim() !== "" &&
        t.destination_name?.trim() !== "" &&
        t.vehicle_id
      );
    });

    if (validTrips.length === 0) {
      toast("Please add at least one trip detail before saving", "ERROR");
      return;
    }
    const firstVehicle = vehicles.find(
      (v) => v.id === validTrips[0].vehicle_id,
    );

    const vendor_id = firstVehicle?.vendor_id;

    const total_amount = validTrips.reduce(
      (sum, t) => sum + (parseFloat(t.amount) || 0),
      0,
    );
    const total_weighment = validTrips.reduce(
      (sum, t) => sum + (parseFloat(t.transition_charge) || 0),
      0,
    );
    const total_advance = validTrips.reduce(
      (sum, t) => sum + (parseFloat(t.advance_amount) || 0),
      0,
    );
    const payable_amount = total_amount + total_weighment - total_advance;

    const billPayload = {
      id: billingId,
      date: date,
      vendor_id: vendor_id,
      vehicle_id: validTrips[0].vehicle_id,
      nor_advance_deduction: 0,
      nor_advance_id: 0,
      res_advance_deduction: 0,
      res_advance_id: 0,
      total_amount: parseFloat(payable_amount.toFixed(2)),
      payable_amount: parseFloat(payable_amount.toFixed(2)),
      remarks: "Generated from billing form",
      status: "new",
      trips: validTrips.map((trip) => ({
        id: trip.id || 0,
        date: trip.date,
        vehicle_id: trip.vehicle_id,
        source_name: trip.source_name,
        destination_name: trip.destination_name,
        total_tones: parseFloat(trip.total_tones) || 0,
        total_km: parseFloat(trip.total_km) || 0,
        rate: parseFloat(trip.rate) || 0,
        amount: parseFloat(trip.amount) || 0,
        transition_charge: parseFloat(trip.transition_charge) || 0,
        vehicle_advance_id: trip.advance_id || null,
        advance_amount: parseFloat(trip.advance_amount) || 0,
        balance_amount: parseFloat(trip.balance_amount) || 0,
        remarks: trip.remarks || "",
        status: "new",
      })),
    };

    try {
      const res = await saveBill(billPayload);

      if (!res.error) {
        toast("Bill saved successfully!", "SUCCESS");

        const clearedTrips = [...Array(50)].map(() => ({
          date: "",
          vehicle_id: "",
          source_name: "",
          destination_name: "",
          rate: 0,
          total_km: 0,
          total_tones: 0,
          amount: 0,
          transition_charge: 0,
          advance_amount: 0,
          balance_amount: 0,
          remarks: "",
        }));

        setBillingTrip(clearedTrips);
        setSelectedVendor(null);
        setFilteredVehicles([]);
        setSelectedVehicle(null);
        setRateType("km");
      } else {
        toast(res.error || "Failed to save bill", "ERROR");
        console.log("res", res.message);
      }
    } catch (err) {
      console.error("Save error:", err);
      toast(err?.error || err?.message || "Error saving bill", "ERROR");
    }
  };

  const handleDelete = async (index) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    const row = billingTrip[index];
    if (!row?.id || row.id === 0) {
      const updated = [...billingTrip];
      updated.splice(index, 1);
      setBillingTrip(updated);
      toast("Row removed", "SUCCESS");
      return;
    }
    try {
      // await deleteBill(billingId);
      await deleteTrip(row.id);
      const updated = [...billingTrip];
      updated.splice(index, 1);
      setBillingTrip(updated);

      toast("Trip deleted Successfully", "SUCCESS");
    } catch (err) {
      toast(err?.message || "Failed to delete bill", "SUCCESS");
    }
  };

  return (
    <Main>
      <PageContainer>
        <CollapseCard title="Billing List">
          <div className="toggle-container mb-3">
            <div style={{ display: "flex", gap: "30px", alignItems: "center" }}>
              <div>
                <label>Date</label>
                <br />
                <input
                  type="date"
                  name="date"
                  value={date}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setDate(newDate);

                    if (selectedVendor?.id) {
                      fetchExistingBill(newDate, selectedVendor.id);
                    }
                  }}
                />
              </div>

              <div>
                <label>Vendor:</label>
                <br />
                <SearchableDropdown
                  value={selectedVendor?.title || ""}
                  onChange={handleVendorChange}
                  allowManual={false}
                  options={vendors.map((v) => ({
                    id: v.id,
                    title: v.name,
                    vendor_id: v.vendor_id,
                  }))}
                />
              </div>
                <div
              style={{
                display: "flex",
                gap: "20px",
                marginTop: "15px",
                marginLeft: "68px",
              }}
            >
              <div className="toggle-switch mt-4">
                <input
                  type="checkbox"
                  id="rateToggle"
                  checked={rateType === "km"}
                  onChange={() =>
                    setRateType(rateType === "km" ? "fixed" : "km")
                  }
                />
                <label htmlFor="rateToggle">
                  <span className="label-text-off">km</span>
                  <span className="slider"></span>
                  <span className="label-text-on">fixed Rate</span>
                </label>
              </div>
            </div>
            </div>
           <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "-50px",
          }}
        >
          <button className="btn btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>   
          </div>
         
          <BillingInformationList
            rateType={rateType}
            handleInputChange={handleInputChange}
            sources={sources}
            destinations={destinations}
            fixSources={fixSources}
            fixDestinations={fixDestinations}
            billingTrip={billingTrip}
            handleDelete={handleDelete}
            selectedVehicle={selectedVehicle}
            handleVehicleChange={handleVehicleChange}
            filteredVehicles={filteredVehicles}
          />
        </CollapseCard>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "10px",
          }}
        >
          <button className="btn btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </PageContainer>
    </Main>
  );
};

export default BillingInformation;

export const SearchableDropdown = forwardRef(
  (
    {
      value,
      rateType,
      onChange,
      options = [],
      onKeyDown,
      allowManual = false,
      customStyle = {},
    },
    ref,
  ) => {
    const [search, setSearch] = useState(value || "");
    const [open, setOpen] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const justSelectedRef = useRef(false);

    const filtered = options.filter((o) =>
      (o.title || "").toLowerCase().includes((search || "").toLowerCase()),
    );

    useEffect(() => {
      setSearch(value || "");
    }, [value]);

    const selectOption = (obj) => {
      justSelectedRef.current = true;
      setSearch(obj.title);
      if (onChange) onChange(obj);
      setOpen(false);
      setHighlightIndex(-1);

      if (onKeyDown) onKeyDown({ key: "Enter", preventDefault: () => {} });
    };

    const handleKeyDown = (e) => {
      let handled = false;

      switch (e.key) {
        case "ArrowDown":
          handled = true;
          e.preventDefault();
          if (!open) setOpen(true);
          setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1));
          break;
        case "ArrowUp":
          handled = true;
          e.preventDefault();
          if (!open) setOpen(true);
          setHighlightIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (highlightIndex >= 0 && filtered[highlightIndex]) {
            selectOption(filtered[highlightIndex]);
          } else {
            if (onKeyDown) onKeyDown(e);
          }
          handled = true;
          return;
        case "Escape":
          setOpen(false);
          break;
        default:
      }

      if (!handled && onKeyDown) {
        onKeyDown(e);
      }
    };

    const handleChange = (e) => {
      const text = e.target.value;
      setSearch(text);

      if (!text.trim()) {
        setOpen(false);
        setHighlightIndex(-1);
        if (onChange) onChange(null);
        return;
      }

      setOpen(true);
      setHighlightIndex(0);
    };

    const commitTypedValue = () => {
      if (!allowManual) return;

      if (justSelectedRef.current) {
        justSelectedRef.current = false;
        return;
      }

      if (search?.trim() && onChange) {
        onChange({ title: search.trim() });
      }
    };

    return (
      <div style={{ position: "relative" }}>
        <input
          type="text"
          ref={ref}
          value={search}
          placeholder="Type to search"
          onFocus={() => {
            if ((search || "").trim() !== "") setOpen(true);
          }}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            commitTypedValue();
            setTimeout(() => setOpen(false), 150);
          }}
          style={{
            width: "150px",
            boxSizing: "border-box",
            ...customStyle,
          }}
        />

        {open && filtered.length > 0 && (
          <ul
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              maxHeight: "180px",
              overflowY: "auto",
              background: "white",
              border: "1px solid #ccc",
              listStyle: "none",
              margin: 0,
              padding: 0,
              zIndex: 100,
            }}
          >
            {filtered.map((item, idx) => (
              <li
                key={item.id ?? idx}
                onMouseDown={() => selectOption(item)}
                style={{
                  padding: "6px 10px",
                  cursor: "pointer",
                  background: highlightIndex === idx ? "#e6f0ff" : "white",
                }}
              >
                {item.title}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  },
);
